(function () {
  'use strict';

  const ASSET_HOST = 'prod-hub-config.8ballpool.com';
  const RUNTIME_URL = 'https://8ballpool.com/scripts/spine-3.7/spine-player.js';
  const RUNTIME_CSS_URL = 'https://8ballpool.com/scripts/spine-3.7/spine-player.css';
  const records = new Map();
  const atlasRequests = new Map();
  let runtimePromise;

  function isTrustedAsset(url, extension) {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'https:'
        && parsed.hostname === ASSET_HOST
        && parsed.pathname.toLowerCase().endsWith(extension);
    } catch (_) {
      return false;
    }
  }

  function loadRuntimeStyles() {
    if (document.querySelector('[data-8bp-spine-runtime-styles]')) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = RUNTIME_CSS_URL;
    link.setAttribute('data-8bp-spine-runtime-styles', '');
    document.head.appendChild(link);
  }

  function loadRuntime() {
    if (window.spine?.SpinePlayer) return Promise.resolve();
    if (runtimePromise) return runtimePromise;

    loadRuntimeStyles();
    runtimePromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('[data-8bp-spine-runtime]');
      if (existingScript) {
        existingScript.addEventListener('load', resolve, { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = RUNTIME_URL;
      script.async = true;
      script.setAttribute('data-8bp-spine-runtime', '');
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    return runtimePromise;
  }

  function getAtlasText(url, signal) {
    if (!atlasRequests.has(url)) {
      atlasRequests.set(url, fetch(url).then((response) => {
        if (!response.ok) throw new Error(`Atlas request failed (${response.status})`);
        return response.text();
      }).catch((error) => {
        atlasRequests.delete(url);
        throw error;
      }));
    }

    if (signal?.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
    return atlasRequests.get(url);
  }

  function dispose(container) {
    visibilityObserver?.unobserve(container);
    const record = records.get(container);
    if (!record) return;

    record.controller.abort();
    if (record.atlasObjectUrl) URL.revokeObjectURL(record.atlasObjectUrl);
    if (record.player && !record.player.disposed) {
      try {
        record.player.dispose();
      } catch (_) {
        // The detached fallback image remains usable even if runtime cleanup fails.
      }
    }
    records.delete(container);
    container.classList.remove('is-loaded');
    delete container.dataset.animationObserved;
  }

  async function initialize(container) {
    if (records.has(container) || container.dataset.animationFailed === 'true') return;

    const jsonUrl = container.dataset.jsonUrl;
    const atlasUrl = container.dataset.atlasUrl;
    const pngUrl = container.dataset.pngUrl;
    if (
      !isTrustedAsset(jsonUrl, '.json')
      || !isTrustedAsset(atlasUrl, '.atlas')
      || !isTrustedAsset(pngUrl, '.png')
    ) {
      container.dataset.animationFailed = 'true';
      return;
    }

    const record = {
      atlasObjectUrl: null,
      container,
      controller: new AbortController(),
      player: null,
      visible: true
    };
    records.set(container, record);

    try {
      const [, atlasText] = await Promise.all([
        loadRuntime(),
        getAtlasText(atlasUrl, record.controller.signal)
      ]);
      if (record.controller.signal.aborted || !container.isConnected) return;

      const atlasLines = atlasText.split(/\r?\n/);
      const pageLine = atlasLines.findIndex((line) => line.trim());
      if (pageLine < 0) throw new Error('Animation atlas is empty');

      const pngFileName = new URL(pngUrl).pathname.split('/').pop();
      atlasLines[pageLine] = pngFileName;
      record.atlasObjectUrl = URL.createObjectURL(new Blob([atlasLines.join('\n')], { type: 'text/plain' }));

      let player;
      player = new window.spine.SpinePlayer(container, {
        jsonUrl,
        atlasUrl: record.atlasObjectUrl,
        pageBaseUrl: pngUrl.slice(0, pngUrl.lastIndexOf('/')),
        animation: 'animation',
        alpha: true,
        backgroundColor: '#00000000',
        showControls: false,
        success() {
          record.player = player;
          if (record.atlasObjectUrl) URL.revokeObjectURL(record.atlasObjectUrl);
          record.atlasObjectUrl = null;

          if (!container.isConnected) {
            dispose(container);
            return;
          }

          container.classList.add('is-loaded');
          player.paused = document.hidden || !record.visible;
        },
        error(_player, reason) {
          if (record.atlasObjectUrl) URL.revokeObjectURL(record.atlasObjectUrl);
          record.atlasObjectUrl = null;
          records.delete(container);
          container.dataset.animationFailed = 'true';
          console.warn('8BP animation could not be loaded; using the static reward image.', reason);
        }
      });
      record.player = player;
    } catch (error) {
      if (record.atlasObjectUrl) URL.revokeObjectURL(record.atlasObjectUrl);
      records.delete(container);
      if (error.name === 'AbortError') return;
      container.dataset.animationFailed = 'true';
      console.warn('8BP animation could not be initialized; using the static reward image.', error);
    }
  }

  const visibilityObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const record = records.get(entry.target);
        if (!record && entry.isIntersecting) {
          initialize(entry.target);
          return;
        }

        if (!record) return;
        record.visible = entry.isIntersecting;
        if (record.player) record.player.paused = document.hidden || !entry.isIntersecting;
      });
    }, { rootMargin: '160px', threshold: 0.01 })
    : null;

  function observe(container) {
    if (container.dataset.animationObserved === 'true') return;
    container.dataset.animationObserved = 'true';
    if (visibilityObserver) {
      visibilityObserver.observe(container);
    } else {
      initialize(container);
    }
  }

  class EightBpAnimation extends HTMLElement {
    connectedCallback() {
      observe(this);
    }

    disconnectedCallback() {
      dispose(this);
    }
  }

  class EightBpCardSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.deal-card-media__track');
      this.viewport = this.querySelector('.deal-card-media__viewport');
      this.pages = Array.from(this.querySelectorAll('.deal-card-media__page'));
      this.previousButton = this.querySelector('[data-deal-slide-prev]');
      this.nextButton = this.querySelector('[data-deal-slide-next]');
      this.pageCount = Number(this.dataset.pageCount) || 1;
      this.pageIndex = 0;
      if (!this.track || this.pageCount < 2) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileLayout = window.matchMedia('(max-width: 1024px)');
      this.hoverTarget = this.closest('.deal-card-media') || this;
      this.start = this.start.bind(this);
      this.stop = this.stop.bind(this);
      this.onPrevious = this.onPrevious.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onLayoutChange = this.onLayoutChange.bind(this);
      this.hoverTarget.addEventListener('mouseenter', this.start);
      this.hoverTarget.addEventListener('mouseleave', this.stop);
      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.viewport?.addEventListener('scroll', this.onScroll, { passive: true });
      this.mobileLayout.addEventListener?.('change', this.onLayoutChange);
      this.syncNavigation();
    }

    disconnectedCallback() {
      this.stop(false);
      this.hoverTarget?.removeEventListener('mouseenter', this.start);
      this.hoverTarget?.removeEventListener('mouseleave', this.stop);
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.mobileLayout?.removeEventListener?.('change', this.onLayoutChange);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    }

    showPage(index, smooth = false) {
      this.pageIndex = Math.max(0, Math.min(this.pageCount - 1, index));
      if (this.mobileLayout.matches && this.viewport) {
        this.track.style.transform = '';
        const page = this.pages[this.pageIndex];
        const viewportRect = this.viewport.getBoundingClientRect();
        const pageRect = page?.getBoundingClientRect();
        const rtl = document.documentElement.dir === 'rtl';
        const delta = pageRect ? (rtl ? pageRect.right - viewportRect.right : pageRect.left - viewportRect.left) : 0;
        this.viewport.scrollTo({
          left: this.viewport.scrollLeft + delta,
          behavior: smooth && !this.reducedMotion.matches ? 'smooth' : 'auto'
        });
      } else {
        const direction = document.documentElement.dir === 'rtl' ? 1 : -1;
        this.track.style.transform = `translate3d(${direction * this.pageIndex * 100}%, 0, 0)`;
      }
      this.syncNavigation();
    }

    syncNavigation() {
      this.previousButton?.classList.toggle('swiper-button-disabled', this.pageIndex === 0);
      this.nextButton?.classList.toggle('swiper-button-disabled', this.pageIndex === this.pageCount - 1);
    }

    changePage(event, step) {
      event.preventDefault();
      event.stopPropagation();
      this.stop(false);
      this.showPage(this.pageIndex + step, true);
    }

    onPrevious(event) {
      this.changePage(event, -1);
    }

    onNext(event) {
      this.changePage(event, 1);
    }

    onScroll() {
      if (!this.mobileLayout.matches || !this.viewport || this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        const viewportRect = this.viewport.getBoundingClientRect();
        const viewportCenter = (viewportRect.left + viewportRect.right) / 2;
        let closestIndex = 0;
        let closestDistance = Infinity;
        this.pages.forEach((page, index) => {
          const rect = page.getBoundingClientRect();
          const distance = Math.abs(((rect.left + rect.right) / 2) - viewportCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });
        this.pageIndex = closestIndex;
        this.syncNavigation();
      });
    }

    onLayoutChange() {
      if (!this.mobileLayout.matches && this.viewport) this.viewport.scrollLeft = 0;
      this.showPage(this.pageIndex);
    }

    start() {
      if (!window.matchMedia('(hover: hover)').matches) return;
      this.stop(false);
      this.showPage((this.pageIndex + 1) % this.pageCount);
      if (this.reducedMotion.matches) return;
      this.advanceTimer = window.setInterval(() => {
        this.showPage((this.pageIndex + 1) % this.pageCount);
      }, 1400);
    }

    stop(reset = true) {
      if (this.advanceTimer) window.clearInterval(this.advanceTimer);
      this.advanceTimer = null;
      if (reset && this.track) this.showPage(0);
    }
  }

  const uniqueIdState = window.EightBpUniqueId;
  const VIP_STORAGE_KEY = uniqueIdState.storageKey;
  const VIP_RECENT_STORAGE_KEY = uniqueIdState.recentStorageKey;
  const VIP_PROFILE_STORAGE_KEY = 'bbirdg:8bp-vip-profile:v1';
  const VIP_PROFILE_TTL = 60 * 60 * 1000;
  const VIP_HIGHLIGHT_DURATION = 1800;
  const VIP_TITLE_MAX_DISPLAY_MULTIPLIER = 5;
  const VIP_API_URL = 'https://vip-calc-306467741271.europe-west1.run.app/calculate-rewards';
  const VIP_DISPLAY_SELECTOR = '[data-vip-card-title], [data-deal-reward-type][data-base-amount]';
  const VIP_CARD_TITLE_SELECTOR = '.prd-name a, .minicart-prd-name a, .prd-prw-title a';
  const VIP_HINT_SELECTOR = '[data-vip-hint]';
  const VIP_LINE_AMOUNT_SELECTOR = '[data-vip-line-amount]';
  const vipCardOriginalTitles = new WeakMap();
  const vipLineProfiles = new Map();
  const vipHighlightTimers = new WeakMap();
  let vipProfileRequest = null;

  function readStoredVipId() {
    return uniqueIdState.read();
  }

  function normalizeVipProfile(data, userId) {
    const multipliers = data?.multipliers;
    if (!multipliers || !['cash', 'coins', 'vip_points'].every((key) => Number(multipliers[key]) > 0)) return null;
    return {
      userId: String(userId),
      username: String(data.username || ''),
      vipTier: String(data.vip_tier || ''),
      multipliers: {
        cash: Number(multipliers.cash),
        coins: Number(multipliers.coins),
        vip_points: Number(multipliers.vip_points)
      },
      verifiedAt: Date.now()
    };
  }

  function readVipProfile(userId = readStoredVipId()) {
    if (!userId) return null;
    try {
      const profile = JSON.parse(window.localStorage.getItem(VIP_PROFILE_STORAGE_KEY) || 'null');
      const verifiedAt = Number(profile?.verifiedAt);
      if (!profile || profile.userId !== userId || !Number.isFinite(verifiedAt) || Date.now() - verifiedAt > VIP_PROFILE_TTL) return null;
      return normalizeVipProfile({
        username: profile.username,
        vip_tier: profile.vipTier,
        multipliers: profile.multipliers
      }, userId) && profile;
    } catch (_) {
      return null;
    }
  }

  function storeVipProfile(data, userId) {
    const profile = normalizeVipProfile(data, userId);
    if (!profile) return null;
    try { window.localStorage.setItem(VIP_PROFILE_STORAGE_KEY, JSON.stringify(profile)); } catch (_) {}
    document.dispatchEvent(new CustomEvent('eight-bp:vip-profile-changed', { detail: { profile } }));
    return profile;
  }

  function clearVipProfile() {
    try { window.localStorage.removeItem(VIP_PROFILE_STORAGE_KEY); } catch (_) {}
    document.dispatchEvent(new CustomEvent('eight-bp:vip-profile-changed', { detail: { profile: null } }));
  }

  async function fetchVipProfileData(userId) {
    const response = await fetch(VIP_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, base_cash: 1, base_coins: 1, base_vip_points: 1 })
    });
    let data = null;
    try { data = await response.json(); } catch (_) { data = null; }
    if (!response.ok || !data) throw new Error('VIP profile lookup failed.');
    return data;
  }

  async function requestVipProfile(userId) {
    if (vipProfileRequest?.userId === userId) return vipProfileRequest.promise;
    const promise = fetchVipProfileData(userId).then((data) => {
      const profile = storeVipProfile(data, userId);
      if (!profile) throw new Error('VIP profile response was invalid.');
      return profile;
    }).finally(() => {
      if (vipProfileRequest?.promise === promise) vipProfileRequest = null;
    });
    vipProfileRequest = { userId, promise };
    return promise;
  }

  function roundHalfToEven(value) {
    const lower = Math.floor(value);
    const fraction = value - lower;
    const tolerance = Number.EPSILON * Math.max(1, Math.abs(value)) * 4;
    if (Math.abs(fraction - 0.5) <= tolerance) return lower % 2 === 0 ? lower : lower + 1;
    return Math.round(value);
  }

  function formatVipCompactNumber(number) {
    if (!Number.isFinite(Number(number))) return '0';
    const value = Number(number);
    if (value >= 1000000) return `${(value / 1000000).toFixed(2).replace(/\.?0+$/, '')}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(2).replace(/\.?0+$/, '')}k`;
    return value.toFixed(2).replace(/\.?0+$/, '');
  }

  function normalizeTitleDigits(value) {
    return String(value).replace(/[٠-٩۰-۹]/g, (digit) => {
      const code = digit.charCodeAt(0);
      return String(code >= 1776 ? code - 1776 : code - 1632);
    });
  }

  function localizedVipInteger(value) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { maximumFractionDigits: 0 }).format(value);
    } catch (_) {
      return Math.round(value).toLocaleString('en-US');
    }
  }

  function findVipTitleAmount(title, baseAmount) {
    const expected = Math.round(Number(baseAmount));
    if (!(expected > 0)) return null;
    const source = String(title);
    const separatorIndex = source.indexOf('|');
    // The 8 in the canonical "| 8 Ball Pool" suffix is game branding, not a
    // reward amount. If a title has that suffix without a separator, it is
    // still safer to leave the title unchanged than infer a reward from it.
    if (separatorIndex < 0 && /\b8\s+Ball\s+Pool\b/i.test(source)) return null;
    const rewardSource = separatorIndex >= 0 ? source.slice(0, separatorIndex) : source;
    const adjustedCandidates = [];
    for (const match of rewardSource.matchAll(/[\p{Nd}][\p{Nd}\s,.'’\u00a0\u066b\u066c\u202f]*/gu)) {
      const value = match[0].replace(/\s+$/, '');
      const numeric = Number(normalizeTitleDigits(value).replace(/\D/g, ''));
      const candidate = { index: match.index, value };
      if (numeric === expected) return { index: match.index, value };
      const multiplier = numeric / expected;
      if (multiplier > 1 && multiplier <= VIP_TITLE_MAX_DISPLAY_MULTIPLIER) {
        adjustedCandidates.push({ ...candidate, multiplier });
      }
    }
    adjustedCandidates.sort((left, right) => left.multiplier - right.multiplier || left.index - right.index);
    // Only a validated reward amount may be replaced. A title without one must
    // remain unchanged; arbitrary numbers can be part of the product name.
    return adjustedCandidates[0] || null;
  }

  function normalizeVipTitleSuffix(value) {
    // Shopify translations can expose encoded separator character codes as text:
    // 124 is "|" and 126 is "~". Only normalize them after the reward amount so
    // a legitimate 124/126 reward amount can never be changed.
    return String(value).replace(/\s+(?:124|126)\s*;?\s+/gu, ' | ');
  }

  function animateVipHighlightLayer(element, role) {
    if (!element) return;
    element.classList.add('vip-highlight-layer');
    element.dataset.vipHighlightRole = role;
    const currentTimer = vipHighlightTimers.get(element);
    if (currentTimer) window.clearTimeout(currentTimer);
    element.classList.remove('is-vip-highlight-animating');
    void element.offsetWidth;
    element.classList.add('is-vip-highlight-animating');
    vipHighlightTimers.set(element, window.setTimeout(() => {
      element.classList.remove('is-vip-highlight-animating');
      element.classList.remove('vip-highlight-layer');
      delete element.dataset.vipHighlightRole;
      vipHighlightTimers.delete(element);
    }, VIP_HIGHLIGHT_DURATION));
  }

  function highlightVipValue(element, animate = true) {
    if (!element) return;
    element.classList.add('vip-value-highlight', 'is-vip-highlighted');
    const badge = element.closest('.deal-card-media__amount, .deal-bundle__amount, .bundle-detail__amount');
    badge?.classList.add('is-vip-adjusted');
    if (!animate) return;
    animateVipHighlightLayer(element, 'text');

    if (!badge) return;
    animateVipHighlightLayer(badge, 'badge');

    const card = badge.closest('.deal-card-media__item, .deal-bundle__item, .bundle-detail');
    if (!card) return;
    animateVipHighlightLayer(card, 'card');

    card.querySelectorAll('.deal-card-media__image, .deal-card-media__spine, .deal-bundle__art, .bundle-detail__art')
      .forEach((art) => animateVipHighlightLayer(art, 'art'));
  }

  function renderVipNumericValue(element, { prefix = '', value = '', suffix = '', highlighted = false, animate = false } = {}) {
    if (!element) return;
    const badge = element.closest('.deal-card-media__amount, .deal-bundle__amount, .bundle-detail__amount');
    if (!highlighted) badge?.classList.remove('is-vip-adjusted');
    const nodes = [document.createTextNode(prefix)];
    if (highlighted) {
      const number = document.createElement('span');
      number.textContent = value;
      nodes.push(number);
      element.replaceChildren(...nodes, document.createTextNode(suffix));
      highlightVipValue(number, animate);
    } else {
      element.replaceChildren(...nodes, document.createTextNode(value), document.createTextNode(suffix));
    }
  }

  function renderVipTitleAmount(title, sourceTitle, baseAmount, amount, animate = false) {
    const match = findVipTitleAmount(sourceTitle, baseAmount);
    if (!match || !Number.isFinite(Number(amount))) return false;
    const formattedAmount = localizedVipInteger(Math.round(Number(amount)));
    const value = document.createElement('span');
    value.textContent = formattedAmount;
    title.replaceChildren(
      document.createTextNode(sourceTitle.slice(0, match.index)),
      value,
      document.createTextNode(normalizeVipTitleSuffix(sourceTitle.slice(match.index + match.value.length)))
    );
    highlightVipValue(value, animate);
    return true;
  }

  function cardRootsWithin(scope = document) {
    const roots = new Set();
    if (scope instanceof Element && scope.matches('[data-vip-card-title]')) roots.add(scope);
    scope.querySelectorAll?.('[data-vip-card-title]').forEach((root) => roots.add(root));
    return roots;
  }

  function restoreVipCardTitles(scope = document) {
    cardRootsWithin(scope).forEach((root) => {
      root.querySelectorAll(VIP_CARD_TITLE_SELECTOR).forEach((title) => {
        const original = vipCardOriginalTitles.get(title);
        if (original) title.innerHTML = original.html;
      });
    });
  }

  function applyVipCardTitles(profile, scope = document, animate = false) {
    if (!profile) return restoreVipCardTitles(scope);
    cardRootsWithin(scope).forEach((root) => {
      const type = root.dataset.vipTitleReward;
      const baseAmount = Number(root.dataset.vipBaseAmount);
      const multiplier = Number(profile.multipliers?.[type]);
      if (!type || !(baseAmount > 0) || !(multiplier > 0)) return;
      const adjustedAmount = roundHalfToEven(baseAmount * multiplier);
      root.querySelectorAll(VIP_CARD_TITLE_SELECTOR).forEach((title) => {
        if (!vipCardOriginalTitles.has(title)) {
          vipCardOriginalTitles.set(title, { html: title.innerHTML, text: title.textContent.trim() });
        }
        const original = vipCardOriginalTitles.get(title);
        renderVipTitleAmount(title, original.text, baseAmount, adjustedAmount, animate);
      });
    });
  }

  function rewardElementsWithin(scope = document) {
    const elements = new Set();
    if (scope instanceof Element && scope.matches('[data-deal-reward-type][data-base-amount]')) elements.add(scope);
    scope.querySelectorAll?.('[data-deal-reward-type][data-base-amount]').forEach((element) => elements.add(element));
    return elements;
  }

  function renderVipRewardBadge(element, amount, adjusted = false, animate = false) {
    const type = element.dataset.dealRewardType;
    if (!['cash', 'coins', 'vip_points'].includes(type)) return;
    const detailed = element.dataset.dealFormat === 'detailed' || type === 'vip_points';
    renderVipNumericValue(element, {
      prefix: element.dataset.dealAmountPrefix || '',
      value: detailed ? localizedVipInteger(Math.round(amount)) : formatVipCompactNumber(amount),
      highlighted: adjusted,
      animate
    });
  }

  function applyVipRewardBadges(profile, scope = document, animate = false) {
    rewardElementsWithin(scope).forEach((element) => {
      const type = element.dataset.dealRewardType;
      if (!['cash', 'coins', 'vip_points'].includes(type)) return;
      const baseAmount = Number(element.dataset.baseAmount);
      const multiplier = Number(profile?.multipliers?.[type]);
      const adjusted = Boolean(profile && baseAmount > 0 && multiplier > 0);
      renderVipRewardBadge(element, adjusted ? roundHalfToEven(baseAmount * multiplier) : baseAmount, adjusted, animate);
    });
  }

  let vipTiers = null;

  // The rewards service names tiers in English ("Black Diamond"). This maps a
  // name to its slug, the game's badge and the name in the page's language.
  function vipTierInfo(tier) {
    if (!vipTiers) {
      try { vipTiers = JSON.parse(document.getElementById('EightBpVipTiers')?.textContent || '{}'); }
      catch (_) { vipTiers = {}; }
    }
    const name = String(tier || '').trim();
    const slug = name.toLowerCase().replace(/\s+/g, '-');
    const known = vipTiers[slug];
    return { slug: known ? slug : '', icon: known?.icon || '', label: known?.label || name };
  }

  function applyVipTierTheme(profile) {
    const slug = vipTierInfo(profile?.vipTier).slug;
    if (slug) document.documentElement.dataset.vipTier = slug;
    else delete document.documentElement.dataset.vipTier;
  }

  function elementsWithin(scope, selector) {
    const elements = new Set();
    if (scope instanceof Element && scope.matches(selector)) elements.add(scope);
    scope.querySelectorAll?.(selector).forEach((element) => elements.add(element));
    return elements;
  }

  // The badge under a VIP product title: a prompt until a Unique ID is verified,
  // then that player's tier.
  function applyVipHints(profile, scope = document) {
    elementsWithin(scope, VIP_HINT_SELECTOR).forEach((hint) => {
      const text = hint.querySelector('[data-vip-hint-text]');
      if (!text) return;
      if (!hint.dataset.vipHintPrompt) hint.dataset.vipHintPrompt = text.textContent.trim();
      const tier = vipTierInfo(profile?.vipTier);
      text.textContent = tier.label ? `${hint.dataset.vipHintTierLabel || ''} ${tier.label}`.trim() : hint.dataset.vipHintPrompt;
      hint.classList.toggle('is-vip-verified', Boolean(tier.label));
      const icon = hint.querySelector('[data-vip-hint-icon]');
      if (icon) {
        if (!icon.dataset.vipHintDefaultSrc) icon.dataset.vipHintDefaultSrc = icon.getAttribute('src') || '';
        icon.src = tier.icon || icon.dataset.vipHintDefaultSrc;
      }
    });
  }

  // A cart line can carry a different Unique ID from the saved one (buying for
  // another account), so its profile is looked up without replacing the saved one.
  function lookupVipLineProfile(userId) {
    const cached = readVipProfile(userId);
    if (cached) return Promise.resolve(cached);
    if (userId === readStoredVipId()) return requestVipProfile(userId);
    if (!vipLineProfiles.has(userId)) {
      vipLineProfiles.set(userId, fetchVipProfileData(userId).then((data) => {
        const profile = normalizeVipProfile(data, userId);
        if (!profile) throw new Error('VIP profile response was invalid.');
        return profile;
      }).catch((error) => {
        vipLineProfiles.delete(userId);
        throw error;
      }));
    }
    return vipLineProfiles.get(userId);
  }

  function renderVipLineAmount(element, profile) {
    const quantity = Math.max(1, Number(element.dataset.quantity) || 1);
    const parts = ['cash', 'coins'].map((type) => {
      const base = Number(element.dataset[type === 'cash' ? 'baseCash' : 'baseCoins']);
      const multiplier = Number(profile.multipliers?.[type]);
      if (!(base > 0) || !(multiplier > 0)) return '';
      const label = element.dataset[type === 'cash' ? 'cashLabel' : 'coinsLabel'] || '';
      return `${localizedVipInteger(roundHalfToEven(base * multiplier) * quantity)} ${label}`.trim();
    }).filter(Boolean);
    const value = element.querySelector('[data-vip-line-amount-value]');
    const tier = element.querySelector('[data-vip-line-amount-tier]');
    if (!value || !parts.length) return;
    value.textContent = parts.join(' + ');
    const tierInfo = vipTierInfo(profile.vipTier);
    if (tier) tier.textContent = tierInfo.label;
    const icon = element.querySelector('[data-vip-line-amount-icon]');
    if (icon) {
      if (tierInfo.icon) icon.src = tierInfo.icon;
      icon.hidden = !tierInfo.icon;
    }
    if (tierInfo.slug) element.dataset.vipTier = tierInfo.slug;
    else delete element.dataset.vipTier;
    element.hidden = false;
  }

  function applyVipLineAmounts(scope = document) {
    elementsWithin(scope, VIP_LINE_AMOUNT_SELECTOR).forEach((element) => {
      const userId = String(element.dataset.vipUserId || '').trim();
      if (!/^\d+$/.test(userId)) return;
      lookupVipLineProfile(userId).then((profile) => {
        if (element.isConnected) renderVipLineAmount(element, profile);
      }).catch((error) => console.error('Theme JS: VIP line amount lookup error:', error));
    });
  }

  // A page shows one VIP bar: the first whose section has VIP amounts on screen.
  function updateVipBars() {
    let shown = false;
    document.querySelectorAll('[data-vip-bar]').forEach((bar) => {
      const displays = bar.closest('.shopify-section')?.querySelectorAll(VIP_DISPLAY_SELECTOR) || [];
      const show = !shown && Array.from(displays).some((display) => !display.closest('.tab-content:not(.active)'));
      bar.hidden = !show;
      shown = shown || show;
    });
  }

  let vipBarsTimer = 0;
  function scheduleVipBarsUpdate() {
    if (vipBarsTimer) return;
    vipBarsTimer = window.setTimeout(() => {
      vipBarsTimer = 0;
      updateVipBars();
    }, 50);
  }

  function applyVipProfile(profile, scope = document, animate = false) {
    applyVipCardTitles(profile, scope, animate);
    applyVipRewardBadges(profile, scope, animate);
    applyVipHints(profile, scope);
    applyVipTierTheme(profile);
  }

  function containsVipDisplay(scope = document) {
    return Boolean(
      (scope instanceof Element && scope.matches(VIP_DISPLAY_SELECTOR))
      || scope.querySelector?.(VIP_DISPLAY_SELECTOR)
    );
  }

  function initializeVipDisplays(scope = document, animate = true) {
    if (!containsVipDisplay(scope)) return;
    const userId = readStoredVipId();
    if (!userId) return applyVipProfile(null, scope);
    const cached = readVipProfile(userId);
    if (cached) {
      applyVipProfile(cached, scope, animate);
    }
    // A calculator on the page looks the stored ID up itself.
    else if (!document.querySelector('eight-bp-vip-calculator')) {
      requestVipProfile(userId).catch((error) => console.error('Theme JS: VIP profile lookup error:', error));
    }
  }

  const vipCardObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      if (containsVipDisplay(node)) initializeVipDisplays(node, false);
      applyVipHints(readVipProfile(), node);
      applyVipLineAmounts(node);
    }));
    scheduleVipBarsUpdate();
  });

  document.addEventListener('eight-bp:vip-profile-changed', (event) => {
    applyVipProfile(event.detail?.profile || null, document, true);
  });
  let vipIdLookupTimer = 0;
  document.addEventListener('eight-bp:vip-id-changed', (event) => {
    window.clearTimeout(vipIdLookupTimer);
    const userId = String(event.detail?.userId || '').trim();
    if (!userId) {
      clearVipProfile();
      return;
    }
    const cached = readVipProfile(userId);
    if (cached) {
      applyVipProfile(cached, document, true);
      return;
    }
    // The amounts on screen belong to the previous ID until this one is verified.
    applyVipProfile(null);
    // A calculator on the page verifies the ID itself and stores the profile.
    if (document.querySelector('eight-bp-vip-calculator') || !document.querySelector(VIP_DISPLAY_SELECTOR)) return;
    // The event fires on every keystroke; look up once typing pauses.
    vipIdLookupTimer = window.setTimeout(() => {
      requestVipProfile(userId).catch((error) => console.error('Theme JS: VIP profile lookup error:', error));
    }, 400);
  });
  window.addEventListener('storage', (event) => {
    if (event.key === VIP_PROFILE_STORAGE_KEY) {
      applyVipProfile(readVipProfile(), document, true);
    }
    else if (event.key === VIP_STORAGE_KEY) {
      if (event.newValue) initializeVipDisplays();
      else {
        restoreVipCardTitles();
        applyVipRewardBadges(null);
      }
    }
  });
  const startVipDisplays = () => {
    initializeVipDisplays();
    const profile = readVipProfile();
    applyVipHints(profile);
    applyVipTierTheme(profile);
    applyVipLineAmounts();
    updateVipBars();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startVipDisplays, { once: true });
  else startVipDisplays();
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('.tabs-item')) scheduleVipBarsUpdate();
    const hint = target?.closest(VIP_HINT_SELECTOR);
    const scope = hint?.closest('[data-vip-bar], [data-section-render], .shopify-section') || document;
    const input = hint && (scope.querySelector('input[data-eight-bp-unique-id]') || scope.querySelector('[data-vip-user-id]'));
    if (!input) return;
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    input.focus({ preventScroll: true });
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) initializeVipDisplays(document, false);
  });
  vipCardObserver.observe(document.documentElement, { childList: true, subtree: true });

  class EightBpVipCalculator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.vipCalculatorReady === 'true') return;
      this.dataset.vipCalculatorReady = 'true';
      // In a VIP bar the calculator only verifies the ID; the badge beside it shows the tier.
      this.profileOnly = this.dataset.mode === 'profile';
      this.rewardScope = this.closest('eight-bp-bundle-details');
      this.descriptionScope = document.querySelector(this.dataset.descriptionScope) || this.closest('[data-section-render]') || document;
      this.userIdInput = this.querySelector('[data-vip-user-id]');
      this.mainButton = this.querySelector('.calc-button');
      this.loading = this.querySelector('[data-vip-loading]');
      this.error = this.querySelector('.product-form__error-message-wrapper');
      this.result = this.querySelector('[data-vip-result]');
      this.recent = this.querySelector('[data-vip-recent-id]');
      this.recentItems = this.querySelector('[data-vip-recent-items]');
      this.helpButton = this.querySelector('[data-vip-help]');
      this.helpTooltip = null;
      this.translations = this.readTranslations();
      this.originalValues = null;
      this.activeUserId = '';
      this.requestSequence = 0;
      this.activeModal = null;
      this.modalPlaceholder = null;
      this.modalTrigger = null;
      this.productTitle = this.descriptionScope.querySelector('[data-vip-product-title]')
        || (this.dataset.dynamicTitleReward ? this.descriptionScope.querySelector('.prd-block-name') : null);
      this.originalProductTitle = this.productTitle?.textContent.trim() || '';

      if (!this.userIdInput || !this.mainButton || !this.loading || !this.error || !this.result) return;

      this.uniqueIdControl = uniqueIdState.enhance(this.userIdInput, {
        source: this.id,
        label: this.translations.uniqueIdLabel,
        clearLabel: this.translations.clear,
        closeLabel: this.translations.close,
        showRecent: false,
        container: this.userIdInput.closest('.eight-bp-vip-calculator__field'),
        before: this.helpButton,
        iconTemplate: this.querySelector('[data-vip-clear-icon]')
      });

      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.onBlur = this.onBlur.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onExternalId = this.onExternalId.bind(this);
      this.onStorage = this.onStorage.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onDocumentReady = this.initializeHelpTooltip.bind(this);
      this.addEventListener('click', this.onClick);
      this.userIdInput.addEventListener('input', this.onInput);
      this.userIdInput.addEventListener('focus', this.onFocus);
      this.userIdInput.addEventListener('click', this.onFocus);
      this.userIdInput.addEventListener('blur', this.onBlur);
      this.querySelector('form')?.addEventListener('submit', this.onSubmit);
      document.addEventListener('eight-bp:vip-id-changed', this.onExternalId);
      window.addEventListener('storage', this.onStorage);
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onKeyDown, true);
      this.initializeHelpTooltip();
      this.initialize();
    }

    disconnectedCallback() {
      this.closeModal(false);
      this.removeEventListener('click', this.onClick);
      this.userIdInput?.removeEventListener('input', this.onInput);
      this.userIdInput?.removeEventListener('focus', this.onFocus);
      this.userIdInput?.removeEventListener('click', this.onFocus);
      this.userIdInput?.removeEventListener('blur', this.onBlur);
      this.querySelector('form')?.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('eight-bp:vip-id-changed', this.onExternalId);
      window.removeEventListener('storage', this.onStorage);
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeyDown, true);
      document.removeEventListener('DOMContentLoaded', this.onDocumentReady);
      this.helpTooltip?.destroy();
    }

    readTranslations() {
      try {
        return JSON.parse(this.querySelector('[data-vip-calculator-translations]')?.textContent || '{}');
      } catch (error) {
        console.error('Theme JS: Invalid 8BP VIP calculator translations.', error);
        return {};
      }
    }

    initializeHelpTooltip() {
      const content = this.helpButton?.dataset.vipHelp;
      if (!content || this.helpTooltip) return;
      if (typeof window.tippy !== 'function') {
        document.addEventListener('DOMContentLoaded', this.onDocumentReady, { once: true });
        return;
      }
      this.helpTooltip = window.tippy(this.helpButton, {
        content,
        theme: document.body.dataset.tooltipColor,
        animation: document.body.dataset.tooltipAnimation,
        duration: [document.body.dataset.tooltipDuration || 300, 0],
        delay: document.body.dataset.tooltipDelay || 0,
        trigger: 'mouseenter focus click',
        touch: true,
        zIndex: 99999
      });
    }

    initialize() {
      this.descriptionScope.querySelectorAll('.rte > #vip-calculator').forEach((calculator) => calculator.remove());
      const descriptionRewards = {};
      ['cash', 'coins', 'vip_points'].forEach((type) => {
        const legacyId = type === 'vip_points' ? '#vipPointsAmount' : `#${type}Amount`;
        const amount = Number(this.dataset[type === 'vip_points' ? 'baseVip' : `base${type[0].toUpperCase()}${type.slice(1)}`]) || 0;
        const element = this.descriptionScope.querySelector(`[data-deal-description-reward="${type}"], ${legacyId}`)
          || this.discoverDescriptionReward(type, amount);
        descriptionRewards[type] = {
          element,
          amount,
          html: element?.innerHTML || '',
          listItem: element?.closest('li'),
          visible: !element?.closest('li') || amount > 0
        };
        if (element) element.dataset.dealDescriptionReward = type;
      });
      this.originalValues = {
        baseCash: descriptionRewards.cash.amount,
        baseCoins: descriptionRewards.coins.amount,
        baseVipPoints: descriptionRewards.vip_points.amount,
        descriptionRewards
      };
      this.resetRewardAmounts();
      this.resetDescriptionRewards();
      this.setButtonMode('calculate');
      const storedUserId = uniqueIdState.read();
      if (storedUserId) {
        this.userIdInput.value = storedUserId;
        this.calculate({ userId: storedUserId, automatic: true, persist: false });
      }
    }

    descriptionRewardAliases(type) {
      const aliases = {
        cash: [this.translations.cash, 'cash'],
        coins: [this.translations.coins, 'coins'],
        vip_points: [this.translations.vipPoints, 'vip points']
      }[type] || [];
      return Array.from(new Set(aliases.map((value) => String(value || '').trim().toLocaleLowerCase()).filter(Boolean)));
    }

    discoverDescriptionReward(type, baseAmount) {
      if (!(baseAmount > 0)) return null;
      const aliases = this.descriptionRewardAliases(type);
      if (!aliases.length) return null;
      const candidates = this.descriptionScope.querySelectorAll('.rte li, .rte p');
      for (const candidate of candidates) {
        if (candidate.closest('eight-bp-vip-calculator, eight-bp-bundle-details')) continue;
        const text = candidate.textContent || '';
        const normalizedText = text.toLocaleLowerCase();
        const labelIndexes = aliases.flatMap((alias) => {
          const indexes = [];
          let index = normalizedText.indexOf(alias);
          while (index >= 0) {
            indexes.push(index);
            index = normalizedText.indexOf(alias, index + alias.length);
          }
          return indexes;
        });
        if (!labelIndexes.length) continue;

        const textNodes = [];
        const walker = document.createTreeWalker(candidate, NodeFilter.SHOW_TEXT);
        let offset = 0;
        while (walker.nextNode()) {
          const node = walker.currentNode;
          const value = node.nodeValue || '';
          textNodes.push({ node, start: offset, end: offset + value.length });
          offset += value.length;
        }

        const amounts = [];
        for (const match of text.matchAll(/[\p{Nd}][\p{Nd}\s,.'’\u00a0\u066b\u066c\u202f]*/gu)) {
          const value = match[0].replace(/\s+$/, '');
          const numeric = Number(normalizeTitleDigits(value).replace(/\D/g, ''));
          if (!(numeric > 0)) continue;
          const multiplier = numeric / baseAmount;
          if (numeric !== Math.round(baseAmount) && !(multiplier >= 1 && multiplier <= VIP_TITLE_MAX_DISPLAY_MULTIPLIER)) continue;
          const distance = Math.min(...labelIndexes.map((labelIndex) => Math.abs(labelIndex - (match.index + value.length))));
          amounts.push({ index: match.index, value, distance, exact: numeric === Math.round(baseAmount) });
        }
        amounts.sort((left, right) => Number(right.exact) - Number(left.exact) || left.distance - right.distance || left.index - right.index);
        const amount = amounts[0];
        if (!amount) continue;
        const textNode = textNodes.find(({ start, end }) => amount.index >= start && amount.index + amount.value.length <= end);
        if (!textNode) continue;

        const localStart = amount.index - textNode.start;
        const before = textNode.node.nodeValue.slice(0, localStart);
        const after = textNode.node.nodeValue.slice(localStart + amount.value.length);
        const marker = document.createElement('span');
        marker.dataset.dealDescriptionReward = type;
        marker.dataset.vipDescriptionNumberOnly = 'true';
        marker.textContent = amount.value;
        textNode.node.replaceWith(document.createTextNode(before), marker, document.createTextNode(after));
        return marker;
      }
      return null;
    }

    formatNumber(number) {
      return formatVipCompactNumber(number);
    }

    formatDetailedNumber(number) {
      return Number.isFinite(Number(number)) ? Math.round(Number(number)).toLocaleString('en-US') : '0';
    }

    escapeHtml(value) {
      return String(value ?? '').replace(/[&<>'"]/g, (character) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
      }[character]));
    }

    renderRewardAmount(element, amount, adjusted = false, shouldHighlight = false) {
      const type = element.dataset.dealRewardType;
      const prefix = ['cash', 'coins', 'token', 'vip_points'].includes(type) ? '' : 'x';
      const formatted = element.dataset.dealFormat === 'detailed'
        ? this.formatDetailedNumber(amount)
        : (type === 'vip_points' ? Number(amount).toLocaleString() : this.formatNumber(amount));
      if (type === 'cue') {
        const cueLabel = amount === 1 ? this.translations.fullCue : this.translations.fullCues;
        element.textContent = `${element.dataset.dealAmountPrefix || ''}x${formatted} (${cueLabel})`;
      } else {
        renderVipNumericValue(element, {
          prefix: `${element.dataset.dealAmountPrefix || ''}${prefix}`,
          value: formatted,
          highlighted: adjusted,
          animate: shouldHighlight
        });
      }
    }

    updateDescriptionReward(type, amount, adjusted = false, shouldHighlight = false) {
      const reward = this.originalValues?.descriptionRewards[type];
      if (!reward?.element) return;
      if (reward.element.dataset.vipDescriptionNumberOnly === 'true') {
        renderVipNumericValue(reward.element, {
          value: localizedVipInteger(Math.round(amount)),
          highlighted: adjusted,
          animate: shouldHighlight
        });
        return;
      }
      renderVipNumericValue(reward.element, {
        prefix: type === 'vip_points' ? '+' : '',
        value: type === 'vip_points' ? Number(amount).toLocaleString() : this.formatNumber(amount),
        suffix: ` ${type === 'vip_points' ? this.translations.vipPoints : this.translations[type]}`,
        highlighted: adjusted,
        animate: shouldHighlight
      });
      if (reward.listItem && type !== 'vip_points') reward.listItem.style.display = amount > 0 ? 'list-item' : 'none';
    }

    updateProductTitle(rewards) {
      const type = this.dataset.dynamicTitleReward;
      if (!type || !this.productTitle || !this.originalProductTitle) return;
      const amount = Number(rewards?.[type]);
      if (!Number.isFinite(amount)) return;
      const baseAmount = Number(this.baseValues()?.[type === 'vip_points' ? 'baseVipPoints' : `base${type[0].toUpperCase()}${type.slice(1)}`]);
      renderVipTitleAmount(this.productTitle, this.originalProductTitle, baseAmount, amount, true);
    }

    resetProductTitle() {
      if (this.productTitle && this.originalProductTitle) this.productTitle.textContent = this.originalProductTitle;
    }

    buildResult(data, baseValues) {
      const baseCash = Math.max(0, Number(baseValues.baseCash) || 0);
      const baseCoins = Math.max(0, Number(baseValues.baseCoins) || 0);
      const baseVipPoints = Math.max(0, Number(baseValues.baseVipPoints) || 0);
      const username = data.username || this.translations.player;
      const tierInfo = vipTierInfo(data.vip_tier);
      const tier = this.escapeHtml(tierInfo.label || this.translations.notAvailable);
      const tierIcon = tierInfo.icon
        ? `<img class="vip-calculator__tier-icon" src="${this.escapeHtml(tierInfo.icon)}" alt="" aria-hidden="true" width="22" height="22">`
        : '';
      const benefits = [];
      const benefitLine = ({ multiplier, label, bonus, total, detailed = false }) => {
        const format = detailed ? (value) => Number(value).toLocaleString() : (value) => this.formatNumber(value);
        return `<li class="vip-calculator__benefit">
          <strong class="vip-calculator__benefit-multiplier">${this.escapeHtml(multiplier)}x ${this.escapeHtml(label)}</strong>
          <span class="vip-calculator__benefit-divider" aria-hidden="true">|</span>
          <span class="vip-calculator__benefit-bonus">+${format(Math.max(0, bonus))}</span>
          <span class="vip-calculator__benefit-arrow" aria-hidden="true">→</span>
          <strong class="vip-calculator__benefit-total"><span data-vip-highlight-value>${this.formatDetailedNumber(total)}</span> ${this.escapeHtml(label)} ${this.escapeHtml(this.translations.total)}</strong>
        </li>`;
      };
      if (baseCash > 0 || data.cash > 0) benefits.push(benefitLine({
        multiplier: data.multipliers?.cash ?? this.translations.notAvailable,
        label: this.translations.cash,
        bonus: data.cash - baseCash,
        total: data.cash
      }));
      if (baseCoins > 0 || data.coins > 0) benefits.push(benefitLine({
        multiplier: data.multipliers?.coins ?? this.translations.notAvailable,
        label: this.translations.coins,
        bonus: data.coins - baseCoins,
        total: data.coins
      }));
      if (baseVipPoints > 0 || data.vip_points > 0) benefits.push(benefitLine({
        multiplier: data.multipliers?.vip_points ?? this.translations.notAvailable,
        label: this.translations.vipPoints,
        bonus: data.vip_points - baseVipPoints,
        total: data.vip_points,
        detailed: true
      }));
      return `<details class="vip-calculator__details" open>
        <summary class="vip-calculator__summary">
          <span class="vip-calculator__welcome">${this.escapeHtml(this.translations.welcome.replace('__PLAYER__', username))}</span>
          <span class="vip-calculator__summary-separator" aria-hidden="true">|</span>
          <span class="vip-calculator__tier">${this.escapeHtml(this.translations.vipTierCompact)} ${tierIcon}<strong>${tier}</strong></span>
          <span class="vip-calculator__chevron" aria-hidden="true"></span>
        </summary>
        <div class="vip-calculator__benefits"><strong class="vip-calculator__benefits-title">${this.escapeHtml(this.translations.vipBenefits)}</strong><ul>${benefits.join('')}</ul></div>
      </details>`;
    }

    baseValues() {
      return this.originalValues || {
        baseCash: Number(this.dataset.baseCash) || 0,
        baseCoins: Number(this.dataset.baseCoins) || 0,
        baseVipPoints: Number(this.dataset.baseVip) || 0
      };
    }

    async request(userId, baseValues) {
      const response = await fetch(this.dataset.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          base_cash: this.profileOnly ? 1 : Math.max(0, Number(baseValues.baseCash) || 0),
          base_coins: this.profileOnly ? 1 : Math.max(0, Number(baseValues.baseCoins) || 0),
          base_vip_points: this.profileOnly ? 1 : Math.max(0, Number(baseValues.baseVipPoints) || 0)
        })
      });
      let data = null;
      try { data = await response.json(); } catch (_) { data = null; }
      if (!response.ok) throw new Error(this.translations.calculationFailed);
      if (!data || !['cash', 'coins', 'vip_points'].every((key) => typeof data[key] === 'number')) {
        throw new Error(this.translations.invalidResponse);
      }
      return data;
    }

    async calculate({ userId: suppliedUserId, automatic = false, persist = true } = {}) {
      const userId = String(suppliedUserId || this.userIdInput.value).trim();
      if (!userId || !/^\d+$/.test(userId)) {
        this.showError(userId ? this.translations.invalidUniqueId : this.translations.enterUniqueId);
        this.userIdInput.setAttribute('aria-invalid', 'true');
        if (automatic) this.removeStoredUserId();
        else this.userIdInput.focus();
        return;
      }
      this.userIdInput.removeAttribute('aria-invalid');
      this.userIdInput.value = userId;
      const sequence = ++this.requestSequence;
      this.setLoading(true);
      this.showError('');
      this.result.style.display = 'none';
      const baseValues = this.baseValues();
      try {
        const data = await this.request(userId, baseValues);
        if (sequence !== this.requestSequence) return;
        storeVipProfile(data, userId);
        ['cash', 'coins', 'vip_points'].forEach((type) => this.updateDescriptionReward(type, data[type], true, true));
        this.updateProductTitle(data);
        if (!this.profileOnly) {
          this.result.innerHTML = this.buildResult(data, baseValues);
          this.result.querySelectorAll('[data-vip-highlight-value]').forEach((element) => highlightVipValue(element, true));
          this.result.style.display = 'block';
        }
        this.activeUserId = userId;
        this.setButtonMode('reset');
        if (persist) this.storeUserId(userId);
      } catch (error) {
        if (sequence !== this.requestSequence) return;
        this.showError(error.message || this.translations.calculationFailed);
        console.error('Theme JS: Calculate Rewards Error:', error);
      } finally {
        if (sequence === this.requestSequence) this.setLoading(false);
      }
    }

    clear({ keepUserId = false, clearStored = true, broadcast = true } = {}) {
      this.requestSequence += 1;
      this.activeUserId = '';
      if (!keepUserId) this.userIdInput.value = '';
      this.resetRewardAmounts();
      this.resetDescriptionRewards();
      this.resetProductTitle();
      this.result.style.display = 'none';
      this.result.innerHTML = '';
      this.showError('');
      this.setLoading(false);
      this.setButtonMode('calculate');
      if (clearStored) this.removeStoredUserId(broadcast);
    }

    resetRewardAmounts() {
      this.rewardScope?.querySelectorAll('[data-deal-reward-type]').forEach((element) => {
        const amount = Number(element.dataset.baseAmount);
        if (Number.isFinite(amount)) this.renderRewardAmount(element, amount);
      });
    }

    resetDescriptionRewards() {
      Object.values(this.originalValues?.descriptionRewards || {}).forEach((reward) => {
        if (!reward.element) return;
        reward.element.innerHTML = reward.html;
        if (reward.listItem) reward.listItem.style.display = reward.visible ? 'list-item' : 'none';
      });
    }

    setButtonMode(mode) {
      this.mainButton.querySelector('[data-vip-button-label]').textContent = mode === 'reset' ? this.translations.reset : this.translations.calculate;
      this.mainButton.dataset.mode = mode;
    }

    // The theme's button loading state: dots replace the label while the lookup runs.
    setLoading(loading) {
      this.mainButton.disabled = loading;
      this.mainButton.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !loading);
      this.loading.textContent = loading ? this.translations.calculating : '';
    }

    showError(message) {
      this.error.querySelector('.product-form__error-message').textContent = message || '';
      this.error.hidden = !message;
    }

    storeUserId(userId) {
      uniqueIdState.set(userId, { source: this.id, persist: true, remember: true });
    }

    removeStoredUserId(broadcast = true) {
      clearVipProfile();
      uniqueIdState.clear({ source: this.id, notify: broadcast });
    }

    readRecentIds() {
      return uniqueIdState.readRecent();
    }

    removeRecentId(userId) {
      uniqueIdState.forgetRecent(userId);
    }

    updateRecentSuggestion(show = false) {
      if (!this.recent || !this.recentItems) return;
      const currentValue = this.userIdInput.value.trim();
      const ids = this.readRecentIds().filter((id) => id !== currentValue);
      this.recentItems.innerHTML = ids.map((id) => `
        <span class="eight-bp-vip-calculator__recent-item">
          <button type="button" class="eight-bp-vip-calculator__recent-select" data-vip-recent-select="${id}"><strong>${id}</strong></button>
          <button type="button" class="eight-bp-vip-calculator__recent-dismiss" data-vip-recent-dismiss="${id}" aria-label="${this.escapeHtml(this.translations.close)}">×</button>
        </span>
      `).join('');
      this.recent.hidden = !show || ids.length === 0;
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('.calc-button')) {
        this.mainButton.dataset.mode === 'reset' ? this.clear() : this.calculate();
        return;
      }
      const recentSelect = target.closest('[data-vip-recent-select]');
      if (recentSelect) {
        const value = recentSelect.dataset.vipRecentSelect;
        if (value) {
          this.userIdInput.value = value;
          this.updateRecentSuggestion(false);
          this.calculate({ userId: value });
        }
        return;
      }
      const recentDismiss = target.closest('[data-vip-recent-dismiss]');
      if (recentDismiss) {
        this.removeRecentId(recentDismiss.dataset.vipRecentDismiss);
        this.updateRecentSuggestion(true);
        this.userIdInput.focus();
        return;
      }
      const trigger = target.closest('[data-vip-club-trigger]');
      if (trigger && !this.closest('eight-bp-bundle-details')) {
        const modal = document.getElementById(trigger.getAttribute('aria-controls'));
        if (modal?.matches('[data-vip-club-modal]')) {
          event.preventDefault();
          this.openModal(modal, trigger);
        }
      }
    }

    onInput() {
      this.userIdInput.removeAttribute('aria-invalid');
      this.showError('');
      const value = this.userIdInput.value.trim();
      this.updateRecentSuggestion(document.activeElement === this.userIdInput);
      if (!value) this.clear();
      else if (this.activeUserId && value !== this.activeUserId) this.clear({ keepUserId: true, clearStored: false });
    }

    onFocus() { this.updateRecentSuggestion(true); }
    onBlur() { window.setTimeout(() => this.updateRecentSuggestion(false), 120); }
    onSubmit(event) { event.preventDefault(); this.mainButton.click(); }

    onExternalId(event) {
      if (event.detail?.source === this.id) return;
      this.applyExternalId(event.detail?.userId || '');
    }

    onStorage(event) {
      if (event.key === VIP_RECENT_STORAGE_KEY) this.updateRecentSuggestion(document.activeElement === this.userIdInput);
      else if (event.key === VIP_STORAGE_KEY) this.applyExternalId(event.newValue?.trim() || '');
    }

    applyExternalId(userId) {
      if (!userId) return this.clear({ clearStored: false, broadcast: false });
      if (!/^\d+$/.test(userId) || userId === this.activeUserId) return;
      this.userIdInput.value = userId;
      this.calculate({ userId, automatic: true, persist: false });
    }

    openModal(modal, trigger) {
      this.activeModal = modal;
      this.modalTrigger = trigger;
      if (modal.parentNode !== document.body) {
        this.modalPlaceholder = document.createComment('8bp-vip-modal');
        modal.before(this.modalPlaceholder);
        document.body.appendChild(modal);
      }
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      modal.querySelector('[data-vip-club-close]')?.focus();
    }

    closeModal(restoreFocus = true) {
      if (!this.activeModal) return;
      this.activeModal.hidden = true;
      this.activeModal.setAttribute('aria-hidden', 'true');
      if (this.modalPlaceholder?.parentNode) this.modalPlaceholder.replaceWith(this.activeModal);
      const trigger = this.modalTrigger;
      this.activeModal = null;
      this.modalPlaceholder = null;
      this.modalTrigger = null;
      if (restoreFocus) trigger?.focus();
    }

    onDocumentClick(event) {
      if (!this.activeModal) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[data-vip-club-close]') || target === this.activeModal) {
        event.preventDefault();
        event.stopPropagation();
        this.closeModal();
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape' && this.activeModal) {
        event.preventDefault();
        event.stopPropagation();
        this.closeModal();
      }
    }
  }

  class EightBpBundleDetails extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bundleDetailsReady === 'true') return;
      this.dataset.bundleDetailsReady = 'true';
      this.vipModalId = this.querySelector('[data-vip-club-modal]')?.id || '';
      this.activeVipModal = null;
      this.activeVipTrigger = null;
      this.vipModalPlaceholder = null;
      this.cueModalIds = new Set(Array.from(this.querySelectorAll('[data-cue-card-modal][id]'), (modal) => modal.id));
      this.activeCueModal = null;
      this.activeCueTrigger = null;
      this.cueModalPlaceholder = null;
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.addEventListener('click', this.onTriggerClick, true);
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onKeyDown, true);
    }

    disconnectedCallback() {
      if (this.activeCueModal) this.closeCueCard(this.activeCueModal, false);
      if (this.activeVipModal) this.closeVipClub(this.activeVipModal, false);
      this.removeEventListener('click', this.onTriggerClick, true);
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeyDown, true);
    }

    onTriggerClick(event) {
      const trigger = event.target.closest('[data-bundle-trigger-index]');
      if (!trigger || !this.contains(trigger)) return;

      const index = trigger.dataset.bundleTriggerIndex;
      const panel = this.querySelector('[data-bundle-details-panel]');
      if (!panel) return;

      panel.querySelectorAll('[data-bundle-detail-index]').forEach((detail) => {
        const isSelected = detail.dataset.bundleDetailIndex === index;
        detail.classList.toggle('is-selected', isSelected);
        if (detail.tagName === 'DETAILS') detail.open = isSelected;
      });

      window.setTimeout(() => {
        const selected = panel.querySelector(`[data-bundle-detail-index="${CSS.escape(index)}"]`);
        panel.scrollTop = selected && index !== '0' ? Math.max(0, selected.offsetTop - 120) : 0;
        selected?.querySelectorAll('eight-bp-animation').forEach((animation) => initialize(animation));
      }, 420);
    }

    onDocumentClick(event) {
      const target = event.target instanceof Element ? event.target : event.target?.parentElement;
      if (!target) return;

      const cueTrigger = target.closest('[data-cue-card-trigger]');
      if (cueTrigger) {
        const modalId = cueTrigger.getAttribute('aria-controls');
        const modal = modalId ? document.getElementById(modalId) : null;
        if (!this.ownsCueModal(modal)) return;
        event.preventDefault();
        event.stopPropagation();
        this.activeCueTrigger = cueTrigger;
        this.openCueCard(modal);
        return;
      }

      const cueClose = target.closest('[data-cue-card-close]');
      if (cueClose) {
        const modal = cueClose.closest('[data-cue-card-modal]');
        if (!this.ownsCueModal(modal)) return;
        event.preventDefault();
        event.stopPropagation();
        this.closeCueCard(modal);
        return;
      }

      const cueModal = target.closest('[data-cue-card-modal]');
      if (cueModal && target === cueModal && this.ownsCueModal(cueModal)) {
        event.preventDefault();
        event.stopPropagation();
        this.closeCueCard(cueModal);
        return;
      }

      const vipTrigger = target.closest('[data-vip-club-trigger]');
      if (vipTrigger) {
        const modal = this.findVipModal(vipTrigger);
        if (!modal) return;
        event.preventDefault();
        event.stopPropagation();
        this.activeVipTrigger = vipTrigger;
        this.openVipClub(modal);
        return;
      }

      const vipClose = target.closest('[data-vip-club-close]');
      if (vipClose) {
        const modal = vipClose.closest('[data-vip-club-modal]');
        if (!this.ownsVipModal(modal)) return;
        event.preventDefault();
        event.stopPropagation();
        this.closeVipClub(modal);
        return;
      }

      const vipModal = target.closest('[data-vip-club-modal]');
      if (vipModal && target === vipModal && this.ownsVipModal(vipModal)) {
        event.preventDefault();
        event.stopPropagation();
        this.closeVipClub(vipModal);
      }
    }

    onKeyDown(event) {
      const target = event.target instanceof Element ? event.target : null;
      const cueTrigger = target?.closest('[data-cue-card-trigger]');
      if ((event.key === 'Enter' || event.key === ' ') && cueTrigger) {
        const modalId = cueTrigger.getAttribute('aria-controls');
        const modal = modalId ? document.getElementById(modalId) : null;
        if (this.ownsCueModal(modal)) {
          event.preventDefault();
          event.stopPropagation();
          this.activeCueTrigger = cueTrigger;
          this.openCueCard(modal);
        }
        return;
      }
      if (event.key !== 'Escape') return;
      if (this.activeCueModal && !this.activeCueModal.hidden) {
        event.preventDefault();
        event.stopPropagation();
        this.closeCueCard(this.activeCueModal);
        return;
      }
      const modal = this.activeVipModal && !this.activeVipModal.hidden
        ? this.activeVipModal
        : document.getElementById(this.vipModalId);
      if (this.ownsVipModal(modal) && !modal.hidden) {
        event.preventDefault();
        event.stopPropagation();
        this.closeVipClub(modal);
      }
    }

    findVipModal(trigger) {
      const panel = trigger.closest('[data-bundle-details-panel]');
      const panelModal = panel?.querySelector('[data-vip-club-modal]');
      if (this.ownsVipModal(panelModal)) return panelModal;

      const controlsId = trigger.getAttribute('aria-controls');
      const referencedModal = controlsId ? document.getElementById(controlsId) : null;
      return this.ownsVipModal(referencedModal) ? referencedModal : null;
    }

    ownsVipModal(modal) {
      return Boolean(this.vipModalId && modal && modal.matches('[data-vip-club-modal]') && modal.id === this.vipModalId);
    }

    ownsCueModal(modal) {
      return Boolean(modal?.matches('[data-cue-card-modal]') && this.cueModalIds.has(modal.id));
    }

    openCueCard(modal) {
      if (!this.ownsCueModal(modal)) return;
      this.activeCueModal = modal;
      if (modal.parentNode !== document.body) {
        this.cueModalPlaceholder = document.createComment('8bp-cue-card-modal');
        modal.before(this.cueModalPlaceholder);
        document.body.appendChild(modal);
      }
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      modal.querySelector('[data-cue-card-close]')?.focus();
    }

    closeCueCard(modal = this.activeCueModal, restoreFocus = true) {
      if (!this.ownsCueModal(modal)) return;
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      if (this.cueModalPlaceholder?.parentNode) this.cueModalPlaceholder.replaceWith(modal);
      const trigger = this.activeCueTrigger;
      this.activeCueModal = null;
      this.activeCueTrigger = null;
      this.cueModalPlaceholder = null;
      if (restoreFocus) trigger?.focus();
    }

    openVipClub(modal) {
      if (!modal) return;
      this.activeVipModal = modal;
      if (modal.parentNode !== document.body) {
        this.vipModalPlaceholder = document.createComment('8bp-vip-modal');
        modal.before(this.vipModalPlaceholder);
        document.body.appendChild(modal);
      }
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      modal.querySelector('[data-vip-club-close]')?.focus();
    }

    closeVipClub(modal = this.activeVipModal, restoreFocus = true) {
      if (!this.ownsVipModal(modal)) return;
      if (!modal) return;
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      const trigger = this.activeVipTrigger?.isConnected
        ? this.activeVipTrigger
        : document.querySelector(`[data-vip-club-trigger][aria-controls="${CSS.escape(this.vipModalId)}"]`);
      if (this.vipModalPlaceholder?.parentNode) {
        this.vipModalPlaceholder.replaceWith(modal);
      }
      this.vipModalPlaceholder = null;
      this.activeVipModal = null;
      this.activeVipTrigger = null;
      if (restoreFocus) trigger?.focus();
    }
  }

  if (!customElements.get('eight-bp-animation')) {
    customElements.define('eight-bp-animation', EightBpAnimation);
  }
  if (!customElements.get('eight-bp-card-slider')) {
    customElements.define('eight-bp-card-slider', EightBpCardSlider);
  }
  if (!customElements.get('eight-bp-bundle-details')) {
    customElements.define('eight-bp-bundle-details', EightBpBundleDetails);
  }
  if (!customElements.get('eight-bp-vip-calculator')) {
    customElements.define('eight-bp-vip-calculator', EightBpVipCalculator);
  }
  document.addEventListener('visibilitychange', () => {
    records.forEach((record) => {
      if (record.player) record.player.paused = document.hidden || !record.visible;
    });
  });

})();
